import { DownOutlined } from "@ant-design/icons";
import { Form, Input, Tooltip } from "antd-v5";
import { FormItemProps } from "antd-v5/lib";
import AuthorisedContainer from "components/AuthorisedContainer";
import message from "components/CustomMessage";
import User from "components/User";
import { PermissionSetMap } from "constants/RBACConstants";
import { capitalize, noop } from "lodash";
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import AssetsAPI from "restAPI/AssetAPI";
import { AssetEsDto } from "types/assetdetails";
import { formatArrayField } from "utils/assetUtils";
import { hasPermission } from "utils/authService";
import { CellRenderers } from "utils/tableUtils";
import { renderBooleanValues } from "views/Findings/FindingsListV3";
import AssetsDetailsPopup from "views/Inventory/Assets/AssetDetails/AssetsDetailPopup";
import { useAssetsDetails } from "views/Inventory/Assets/AssetDetails/AssetsDetailsProvider";
import { AssetModalCommonConfig } from "views/Inventory/Assets/AssetDetails/common";
import { ASSET_TYPE_FIELD_DISPLAY_MAP } from "views/Inventory/Assets/constants";

import ACDropdown from "ac-components/ACDropdown";
import ACLink from "ac-components/ACLink/ACLink";
import CheckIcon from "ac-components/ac-icons/CheckIcon";
import EditIcon from "ac-components/ac-icons/EditIcon";
import TrashIcon from "ac-components/ac-icons/TrashIcon";
import { NOT_AVAILABLE } from "ac-components/ac-na-component/ac-na-component";
import ACTagColored from "ac-components/ac-tag-colored/ACTagColored";
import ACTag from "ac-components/ac-tag/ACTag";

import useModalRenderer from "./useModalRenderer";

const ACTIVE_VALUE = "Active";
const INACTIVE_VALUE = "Inactive";

type AssetDetails = {
  id: string | number;
};

interface UseAssetDetailOverviewFieldsOptions {
  isCorrelatedAsset?: boolean;
}

export const useAssetDetailOverviewFields = (
  assetDetails: AssetEsDto,
  options: UseAssetDetailOverviewFieldsOptions = {},
) => {
  const { isCorrelatedAsset = false } = options;
  const _status = isCorrelatedAsset ? assetDetails.merged : assetDetails.active;
  const [status, setStatus] = useState(_status ? ACTIVE_VALUE : INACTIVE_VALUE);
  const [isEditingAssetName, setIsEditingAssetName] = useState(false);
  const [assetName, setAssetName] = useState(
    CellRenderers.VALUE_OR_NA(assetDetails.name),
  );
  const [validateNameStatus, setValidateNameStatus] =
    useState<FormItemProps["validateStatus"]>("success");
  const { refreshDetails } = useAssetsDetails();
  const location = useLocation();

  useEffect(() => {
    const _status = isCorrelatedAsset
      ? assetDetails.merged
      : assetDetails.active;
    setStatus(_status ? ACTIVE_VALUE : INACTIVE_VALUE);
    setAssetName(assetDetails.name ?? "");
  }, [assetDetails.active, assetDetails.name]);

  const onOptionClick = useCallback(
    async (value: string | number) => {
      const messageReference = message.loading("Updating...");
      await AssetsAPI.updateAssetDetails(assetDetails.id, { active: value })
        .promise;
      messageReference();
      setStatus(value === "true" ? ACTIVE_VALUE : INACTIVE_VALUE);
      refreshDetails();
      message.success("Asset details updated successfully");
    },
    [assetDetails.id, refreshDetails],
  );

  const toggleEdit = () => setIsEditingAssetName((prev) => !prev);

  const handleValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setAssetName(inputValue);
    if (inputValue.length < 3) {
      setValidateNameStatus("error");
    } else {
      setValidateNameStatus("success");
    }
  };

  const handleSave = useCallback(async () => {
    toggleEdit();
    const messageReference = message.loading("Updating...");
    await AssetsAPI.updateAssetDetails(assetDetails.id, { name: assetName })
      .promise;
    refreshDetails();
    messageReference();
    message.success("Asset details updated successfully");
  }, [assetDetails.id, assetName, refreshDetails]);

  const onCancelEditName = useCallback(() => {
    setAssetName(assetDetails.name);
    setValidateNameStatus("");
    toggleEdit();
  }, [assetDetails.name]);

  const hasError = validateNameStatus === "error";

  const fields = useMemo(
    () => [
      {
        label: "Name",
        name: "assetName",
        valueClassName: "break-content",
        span: 24,
        formatter: () => {
          return (
            <>
              {isEditingAssetName ? (
                <Form.Item
                  validateStatus={validateNameStatus}
                  style={{ padding: 0, margin: 0 }}
                >
                  <Input value={assetName} onChange={handleValueChange} />
                </Form.Item>
              ) : (
                <span title={assetName}>
                  {CellRenderers.VALUE_OR_NA(assetName)}
                </span>
              )}
            </>
          );
        },
        extra: isCorrelatedAsset ? null : (
          <>
            {isEditingAssetName ? (
              <Tooltip
                title={
                  hasError ? "Name must be at least 3 characters long" : ""
                }
              >
                <CheckIcon
                  onClick={!hasError ? handleSave : noop}
                  className={hasError ? "disabled" : ""}
                  style={{ fontSize: 18 }}
                />
                <TrashIcon
                  onClick={onCancelEditName}
                  style={{ fontSize: 18 }}
                />
              </Tooltip>
            ) : (
              <AuthorisedContainer permission={PermissionSetMap.UPDATE_ASSETS}>
                <EditIcon onClick={toggleEdit} />
              </AuthorisedContainer>
            )}
          </>
        ),
      },
      {
        label: "ID",
        name: "assetID",
        span: 24,
        formatter: () => {
          if (!assetDetails.id) return NOT_AVAILABLE;
          const isOnAssetsPage = location.pathname.endsWith("/assets");
          return isOnAssetsPage ? (
            assetDetails.id
          ) : (
            <AssetLink assetDetails={assetDetails} />
          );
        },
      },
      {
        label: "Type",
        name: "armorCodeType",
        span: 24,
        formatter: () => {
          return assetDetails.armorCodeType ? (
            //@ts-ignore
            <ACTag mode="label">
              {ASSET_TYPE_FIELD_DISPLAY_MAP[assetDetails.armorCodeType] ??
                capitalize(assetDetails.armorCodeType)}
            </ACTag>
          ) : (
            CellRenderers.VALUE_OR_NA()
          );
        },
      },
      {
        label: "Source",
        name: "source",
        span: 24,
        valueClassName: "break-content",
        formatter: () => {
          return formatArrayField(assetDetails.source);
        },
      },
      {
        label: "First Seen",
        name: "firstSeen",
        span: 24,
        formatter: () => CellRenderers.DATE(assetDetails.firstSeen),
      },
      {
        label: "Status",
        name: "status",
        span: 24,
        formatter: () => {
          return (
            <>
              {
                <ACTagColored
                  color={status === "Active" ? "Success" : "Neutral"}
                  label={status}
                />
              }
            </>
          );
        },
        extra: isCorrelatedAsset ? null : (
          <ACDropdown
            onOptionClick={onOptionClick}
            placement="left"
            items={[
              {
                label: <ACTagColored color={"Success"} label={"Active"} />,
                value: "true",
                secondaryIcon: status === "Active" ? <CheckIcon /> : undefined,
                disabled: !hasPermission(PermissionSetMap.UPDATE_ASSETS),
              },
              {
                label: <ACTagColored color={"Neutral"} label={"Inactive"} />,
                value: "false",
                secondaryIcon:
                  status === "Inactive" ? <CheckIcon /> : undefined,
                disabled: !hasPermission(PermissionSetMap.UPDATE_ASSETS),
              },
            ]}
          >
            <DownOutlined style={{ fontSize: 9 }} />
          </ACDropdown>
        ),
      },
      {
        label: "Hierarchy",
        name: "hierarchy",
        span: 24,
        containerClassName: "flex-column",
        formatter: () => {
          return CellRenderers.VALUE_OR_NA(assetDetails.hierarchy);
        },
      },
      {
        label: "Location",
        name: "assetLocations",
        span: 24,
        valueClassName: "break-content",
        formatter: () => formatArrayField(assetDetails.location),
      },
      {
        label: "Scanned",
        name: "scanned",
        span: 24,
        valueClassName: "break-content",
        formatter: () => {
          return renderBooleanValues(assetDetails.scanned);
        },
      },
      {
        label: "Asset Owner",
        name: "owner",
        pinnableFieldName: "assetOwner",
        span: 24,
        valueClassName: "break-content",
        formatter: () => {
          const owner = assetDetails.owner;
          if (owner) {
            return <User userEmail={owner} />;
          }
          return CellRenderers.VALUE_OR_NA();
        },
      },
    ],
    [
      status,
      assetDetails,
      onOptionClick,
      handleSave,
      isEditingAssetName,
      assetName,
      onCancelEditName,
      hasError,
      validateNameStatus,
      location.pathname,
      isCorrelatedAsset,
    ],
  );

  return fields;
};

function AssetLink({ assetDetails }: { assetDetails: AssetDetails }) {
  const modalRenderer = useModalRenderer();

  const showAssetDetail = useCallback(
    (assetID) => {
      modalRenderer.show({
        content: <AssetsDetailsPopup assetID={assetID} />,
        ...AssetModalCommonConfig,
      });
    },
    [modalRenderer],
  );

  return (
    <>
      <ACLink
        onClick={() => {
          if (showAssetDetail) {
            showAssetDetail(Number(assetDetails.id));
          }
        }}
      >
        {assetDetails.id}
      </ACLink>
      {modalRenderer.context}
    </>
  );
}
